<!-- Copyright 2020 Adobe. All rights reserved.
This file is licensed to you under the Apache License, Version 2.0 (the "License");
you may not use this file except in compliance with the License. You may obtain a copy
of the License at http://www.apache.org/licenses/LICENSE-2.0
Unless required by applicable law or agreed to in writing, software distributed under
the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
OF ANY KIND, either express or implied. See the License for the specific language
governing permissions and limitations under the License. -->

import {Layout} from '@react-spectrum/docs';
export default Layout;

import docs from 'docs:@react-spectrum/picker';
import {HeaderInfo, PropTable} from '@react-spectrum/docs';
import packageData from '../package.json';

```jsx import
import {Flex} from '@react-spectrum/layout';
import {Picker, Item, Section} from '@react-spectrum/picker';
import Book from '@spectrum-icons/workflow/Book';
import BulkEditUsers from '@spectrum-icons/workflow/BulkEditUsers';
import Draw from '@spectrum-icons/workflow/Draw';
import ColorPalette from '@spectrum-icons/workflow/ColorPalette';
import AlignLeft from '@spectrum-icons/workflow/AlignLeft';
import {Text} from '@react-spectrum/typography';
```

---
category: Pickers
---

# Picker

<p>{docs.exports.Picker.description}</p>

<HeaderInfo
  packageData={packageData}
  componentNames={['Picker', 'Item', 'Section']}
  sourceData={[
    {type: 'Spectrum', url: 'https://spectrum.adobe.com/page/dropdown/'}
  ]} />

## Example
```tsx example
<Picker label="Choose frequency" onSelectionChange={selected => console.log(selected)}>
  <Item uniqueKey="rarely">Rarely</Item>
  <Item uniqueKey="sometimes">Sometimes</Item>
  <Item uniqueKey="always">Always</Item>
</Picker>
```

## Content
Picker follows the [Collection Components](../react-stately/collections.html) API, accepting both static and dynamic forms of children. Similar to the Menu, Picker accepts an `<Item>` component, each with a `uniqueKey` prop. Basic usage of the Picker, seen in the example above, shows multiple options populated with a string. This implementation would be used when the list of options is finite and known.

The dynamic method (shown below) would be better suited to use if the options came from a data object such as values returned from an API call. Providing the data in this way allows for Picker to automatically cache the rendering of each item, which dramatically improves performance.

As seen below, an iterable list of options is passed to the Picker using the `items` prop. Each item accepts a uniqueKey prop, which is passed to the onSelectionChange handler to identify the selected item. Alternatively, if the item objects contain an id property, as shown in the example below, then this is used automatically and a uniqueKey prop is not required. See the <a href="#events" title="Events">events</a> section for more detail on selection.

```tsx example
function Example() {
  let options = [
    {id: 1, name: 'Aardvark'},
    {id: 2, name: 'Cat'},
    {id: 3, name: 'Dog'},
    {id: 4, name: 'Kangaroo'},
    {id: 5, name: 'Koala'},
    {id: 6, name: 'Penguin'},
    {id: 7, name: 'Snake'},
    {id: 8, name: 'Turtle'},
    {id: 9, name: 'Wombat'}
  ];

  return (
    // Dynamic Items
    <Picker label="Pick an animal" items={options} onSelectionChange={selected => console.log(selected)}>
      {item => <Item>{item.name}</Item>}
    </Picker>
  );
}
```

### Internationalization
To internationalize a Picker, a localized string should be passed to the `children` of each `Item`.
For languages that are read right to left (e.g. Hebrew and Arabic), the layout of the Picker is flipped.

## Labeling
Picker can be labeled using the `label` prop. If no label is provided, an alternative text label must be provided to identify the control for accessibility. This should be added using the `aria-label` prop. If the Picker is a required field, the `isRequired` and `necessityIndicator` props can be used to show a required state.

```tsx example
<Picker label="Choose frequency" isRequired necessityIndicator="icon" marginEnd="10px">
  <Item uniqueKey="rarely">Rarely</Item>
  <Item uniqueKey="sometimes">Sometimes</Item>
  <Item uniqueKey="always">Always</Item>
</Picker>
<Picker label="Choose frequency" isRequired necessityIndicator="label" marginEnd="10px">
  <Item uniqueKey="rarely">Rarely</Item>
  <Item uniqueKey="sometimes">Sometimes</Item>
  <Item uniqueKey="always">Always</Item>
</Picker>
<Picker label="Choose frequency" necessityIndicator="label">
  <Item uniqueKey="rarely">Rarely</Item>
  <Item uniqueKey="sometimes">Sometimes</Item>
  <Item uniqueKey="always">Always</Item>
</Picker>
```

## Selection
Setting a selected option can be done by using the `defaultSelectedKey` or `selectedKey` prop. The selected key corresponds to the `uniqueKey` of an item. See <a href="#events" title="Events">Events</a> for more details on selection events.

```tsx example
function Example() {
  let options = [
    {name: 'Koala'},
    {name: 'Kangaroo'},
    {name: 'Platypus'},
    {name: 'Bald Eagle'},
    {name: 'Bison'},
    {name: 'Skunk'}
  ];
  let [animal, setAnimal] = React.useState("Bison");

  return (
    <>
      <Picker
        label="Pick an animal (uncontrolled)"
        items={options}
        defaultSelectedKey="Bison"
        marginEnd="20px">
        {item => <Item uniqueKey={item.name}>{item.name}</Item>}
      </Picker>

      <Picker
        label="Pick an animal (controlled)"
        items={options}
        selectedKey={animal}
        onSelectionChange={selected => setAnimal(selected)}>
        {item => <Item uniqueKey={item.name}>{item.name}</Item>}
      </Picker>
    </>
  );
}
```
## Sections
Picker supports sections in order to group options. Sections can be used by wrapping groups of Items in a `Section` component. Each `Section` takes a `title` and `uniqueKey` prop.

### Static Items
```tsx example
<Picker label="Pick your favorite">
  <Section title="Animals">
    <Item>Aardvark</Item>
    <Item>Kangaroo</Item>
    <Item>Snake</Item>
  </Section>
  <Section title="People">
    <Item>Danni</Item>
    <Item>Devon</Item>
    <Item>Ross</Item>
  </Section>
</Picker>
```
### Dynamic Items
Sections used with dynamic items are populated from a hierarchical data structure. Similarly to the props on `Picker`, `Section` takes an array of data using the `items` prop.

```tsx example
function Example() {
  let options = [
    {name: 'Australian', children: [
      {id: 2, name: 'Koala'},
      {id: 3, name: 'Kangaroo'},
      {id: 4, name: 'Platypus'}
    ]},
    {name: 'American', children: [
      {id: 6, name: 'Bald Eagle'},
      {id: 7, name: 'Bison'},
      {id: 8, name: 'Skunk'}
    ]}
  ];

  return (
    <>
      <Picker label="Pick an animal" items={options} onSelectionChange={selected => alert(selected)}>
        {item => (
          <Section uniqueKey={item.name} items={item.children} title={item.name}>
            {item => <Item>{item.name}</Item>}
          </Section>
        )}
      </Picker>
    </>
  );
}
```

## Events
Picker supports selection via mouse, keyboard, and touch. You can handle all of these via the `onSelectionChange`
prop. Picker will pass the selected `uniqueKey` to the `onSelectionChange` handler.

The following example uses an `onSelectionChange` handler to update the selection stored in React state.

```tsx example
function StaticExample() {
  let [frequency, setFrequency] = React.useState();

  return (
    <>
      <Picker label="Choose frequency" onSelectionChange={selected => setFrequency(selected)}>
        <Item uniqueKey="Rarely">Rarely</Item>
        <Item uniqueKey="Sometimes">Sometimes</Item>
        <Item uniqueKey="Always">Always</Item>
      </Picker>
      <p>You selected {frequency}</p>
    </>
  );
}
```

When using Picker with dynamic items, selection works much the same way using `uniqueKey`. However, if your data already has an `id` property (as is common with many data sets), Picker can use that id without needing to specify a `uniqueKey` prop. The below example shows Picker using the id of each item from the `items` array as the selected value without the need for `uniqueKey`. Note that `uniqueKey` will always take precedence if set.

```tsx example
function DynamicExample() {
  let [animalId, setAnimalId] = React.useState();
  let options = [
    {id: 1, name: 'Aardvark'},
    {id: 2, name: 'Cat'},
    {id: 3, name: 'Dog'},
    {id: 4, name: 'Kangaroo'},
    {id: 5, name: 'Koala'},
    {id: 6, name: 'Penguin'},
    {id: 7, name: 'Snake'},
    {id: 8, name: 'Turtle'},
    {id: 9, name: 'Wombat'}
  ];

  return (
    <>
      <Picker label="Pick an animal" items={options} onSelectionChange={selected => setAnimalId(selected)}>
        {item => <Item>{item.name}</Item>}
      </Picker>
      <p>Your favorite animal has id: {animalId}</p>
    </>
  );
}
```

## Complex Items
Items within Picker also allow for additional content used to better communicate options. Icons and descriptions can be added to the `children` of `Item` as shown in the example below. If a description is added, the prop `slot="description"` must be used to distinguish the different `<Text>` elements.

```tsx example
<Picker label="Options">
  <Section title="Permission">
    <Item textValue="Read">
      <Book size="S" />
      <Text>Read</Text>
      <Text slot="description">Read Only</Text>
    </Item>
    <Item textValue="Write">
      <Draw size="S" />
      <Text>Write</Text>
      <Text slot="description">Read and Write Only</Text>
    </Item>
    <Item textValue="Admin">
      <BulkEditUsers size="S" />
      <Text>Admin</Text>
      <Text slot="description">Full access</Text>
    </Item>
  </Section>
</Picker>
```

## Props

<PropTable component={docs.exports.Picker} links={docs.links} />

## Visual Options

### Label Alignment
```tsx example
<Picker label="Choose frequency" labelAlign="end">
  <Item uniqueKey="rarely">Rarely</Item>
  <Item uniqueKey="sometimes">Sometimes</Item>
  <Item uniqueKey="always">Always</Item>
</Picker>
```
### Label Position
```tsx example
<Picker label="Choose frequency" labelPosition="side">
  <Item uniqueKey="rarely">Rarely</Item>
  <Item uniqueKey="sometimes">Sometimes</Item>
  <Item uniqueKey="always">Always</Item>
</Picker>
```
### Quiet
```tsx example
<Picker label="Choose frequency" isQuiet labelPosition="side">
  <Item uniqueKey="rarely">Rarely</Item>
  <Item uniqueKey="sometimes">Sometimes</Item>
  <Item uniqueKey="always">Always</Item>
</Picker>
```
### Disabled
```tsx example
<Picker label="Choose frequency" isDisabled>
  <Item uniqueKey="rarely">Rarely</Item>
  <Item uniqueKey="sometimes">Sometimes</Item>
  <Item uniqueKey="always">Always</Item>
</Picker>
```
### Custom widths
```tsx example
<Flex flexDirection="column">
  <Picker label="Choose frequency" width="size-6000">
    <Item uniqueKey="rarely">Rarely</Item>
    <Item uniqueKey="sometimes">Sometimes</Item>
    <Item uniqueKey="always">Always</Item>
  </Picker>

  <Picker label="Choose animal" menuWidth="size-6000">
    <Item uniqueKey="Emu">Emu</Item>
    <Item uniqueKey="Kangaroo">Kangaroo</Item>
    <Item uniqueKey="Platypus">Platypus</Item>
  </Picker>
</Flex>
```
### Align and direction
```tsx example
<Flex flexDirection="column">
  <Picker label="Choose frequency" align="end" menuWidth="size-3000">
    <Item uniqueKey="rarely">Rarely</Item>
    <Item uniqueKey="sometimes">Sometimes</Item>
    <Item uniqueKey="always">Always</Item>
  </Picker>

  <Picker label="Choose animal" direction="top">
    <Item uniqueKey="Emu">Emu</Item>
    <Item uniqueKey="Kangaroo">Kangaroo</Item>
    <Item uniqueKey="Platypus">Platypus</Item>
  </Picker>
</Flex>
```
### Menu state
The open state of the menu can be controlled via the `defaultOpen` and `isOpen` props
```tsx example
<Picker label="Choose frequency" isOpen>
  <Item uniqueKey="rarely">Rarely</Item>
  <Item uniqueKey="sometimes">Sometimes</Item>
  <Item uniqueKey="always">Always</Item>
</Picker>
```
